import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp } from 'react-feather';
import { styled } from '@mui/material/styles';

/**
 * Text that fills the height it's given and shows in full when it fits. "See more" appears only when
 * the space is shorter than the text (e.g. a page builder row dragged to a fixed height); expanding
 * shows it all, and the cell scrolls if needed.
 */
export function FitText({ children }: { children: React.ReactNode }): React.ReactElement {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    // 1 px allowance for subpixel rounding.
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, expanded]);

  return (
    <Root>
      <Body ref={bodyRef} expanded={expanded}>
        {children}
      </Body>
      {!expanded && overflows && (
        <Toggle type="button" onClick={() => setExpanded(true)}>
          ...See more
        </Toggle>
      )}
      {expanded && (
        <Toggle type="button" aria-label="See less" onClick={() => setExpanded(false)}>
          <ChevronUp size={16} />
        </Toggle>
      )}
    </Root>
  );
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  lineHeight: '15px',
});

const Body = styled('div', { shouldForwardProp: (p) => p !== 'expanded' })<{ expanded: boolean }>(({ expanded }) => ({
  flex: expanded ? 'none' : 1,
  minHeight: 0,
  overflow: expanded ? 'visible' : 'hidden',
}));

const Toggle = styled('button')(({ theme }) => ({
  alignSelf: 'flex-end',
  marginTop: 4,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  font: 'inherit',
  fontStyle: 'normal',
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' },
}));
